import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarRange, CheckCircle2, Clock, Loader2, RefreshCw, Info, Pencil, Save, X } from "lucide-react";
import { api } from "../../lib/api";
import WeeklyWorkPlan from "./WeeklyWorkPlan";

const euro = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const fmt = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : "—");
const STATUS = {
  completata: { label: "Completata", bar: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  in_corso: { label: "In corso", bar: "bg-sky-500", chip: "bg-sky-50 text-sky-700 border-sky-200", icon: Clock },
  in_ritardo: { label: "In ritardo", bar: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  pianificata: { label: "Pianificata", bar: "bg-slate-400", chip: "bg-slate-50 text-slate-600 border-slate-200", icon: CalendarRange },
};

const dayDiff = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

const monthTicks = (start, end) => {
  const ticks = [];
  const s = new Date(start), e = new Date(end), span = Math.max(dayDiff(start, end), 1);
  const d = new Date(s.getFullYear(), s.getMonth() + 1, 1);
  while (d < e) {
    ticks.push({ left: (dayDiff(s, d) / span) * 100, label: d.toLocaleDateString("it-IT", { month: "short", year: "2-digit" }) });
    d.setMonth(d.getMonth() + 1);
  }
  return ticks;
};

const Summary = ({ s, data }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gantt-summary">
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs text-slate-500">Avanzamento lavori</div>
      <div className="font-mono text-xl font-bold text-slate-900" data-testid="gantt-progress">{s.progress}%</div>
      <div className="text-[11px] text-slate-400">atteso {s.expected}% · {euro(s.done_amount)} su {euro(s.amount)}</div>
    </div>
    <div className={`bg-white border rounded-xl p-4 ${s.late > 0 ? "border-red-200" : "border-slate-200"}`}>
      <div className="text-xs text-slate-500">Fasi in ritardo</div>
      <div className={`font-mono text-xl font-bold ${s.late > 0 ? "text-red-700" : "text-slate-900"}`} data-testid="gantt-late-count">{s.late}<span className="text-sm text-slate-400 font-normal"> / {s.phases}</span></div>
      <div className="text-[11px] text-slate-400">{s.behind_pts > 0 ? `${s.behind_pts} punti sotto il previsto` : "in linea con il programma"}</div>
    </div>
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs text-slate-500">Fasi completate</div>
      <div className="font-mono text-xl font-bold text-emerald-700" data-testid="gantt-completed-count">{s.completed}<span className="text-sm text-slate-400 font-normal"> / {s.phases}</span></div>
    </div>
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs text-slate-500">{s.days_left >= 0 ? "Giorni alla fine prevista" : "Giorni oltre la fine prevista"}</div>
      <div className={`font-mono text-xl font-bold ${s.days_left < 0 ? "text-red-700" : "text-slate-900"}`} data-testid="gantt-days-left">{Math.abs(s.days_left)}</div>
      <div className="text-[11px] text-slate-400">{fmt(data.start)} → {fmt(data.end)}</div>
    </div>
  </div>
);

const WorkRow = ({ work, data, span }) => {
  const left = Math.max(0, Math.min(100, dayDiff(data.start, work.planned_start) / span * 100));
  const width = Math.max(0, Math.min(100-left, Math.max(1.5, (dayDiff(work.planned_start, work.planned_end)+1) / span * 100)));
  const color = ({done:"bg-emerald-500",in_progress:"bg-sky-500",blocked:"bg-red-500",planned:"bg-amber-500"})[work.status] || "bg-slate-400";
  return <div className="grid grid-cols-[minmax(160px,260px)_1fr] gap-3 items-center py-1 border-t border-slate-50" data-testid="gantt-work-row"><div className="pl-5 text-xs text-slate-600 truncate" title={work.title}>{work.title}<span className="text-slate-400 ml-1">{work.responsible || ""}</span></div><div className="relative h-5"><div className={`absolute top-1 h-3 rounded ${color}`} style={{left:`${left}%`,width:`${width}%`}} title={`${work.title}: ${work.planned_start} → ${work.planned_end}`} /></div></div>;
};

const PhaseRow = ({ p, data, span, onEdit }) => {
  const st = STATUS[p.status];
  const left = Math.min(Math.max((dayDiff(data.start, p.planned_start) / span) * 100, 0), 100);
  const width = Math.min(Math.max((dayDiff(p.planned_start, p.planned_end) / span) * 100, 1.5), 100 - left);
  const late = p.status === "in_ritardo";
  return (
    <div className={`grid grid-cols-[minmax(160px,260px)_1fr] gap-3 items-center py-2 border-t border-slate-100 ${late ? "bg-red-50/40" : ""}`} data-testid={`gantt-phase-${p.name}`} data-status={p.status}>
      <div className="min-w-0 pl-2">
        <div className="flex items-center gap-1"><div className="text-sm font-medium text-slate-800 truncate" title={p.name}>{p.name}</div><button type="button" onClick={() => onEdit(p)} aria-label={`Pianifica ${p.name}`} title="Modifica date pianificate" className="shrink-0 p-1 text-slate-500 hover:text-orange-600"><Pencil className="w-3.5 h-3.5" /></button></div>
        <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-semibold ${st.chip}`}><st.icon className="w-3 h-3" /> {st.label}{p.delay_days > 0 ? ` +${p.delay_days}g` : ""}</span>
          <span className="font-mono">{p.done}/{p.items} voci</span>{p.manual_dates && <span title="Date impostate manualmente" className="text-orange-700">manuale</span>}
        </div>
      </div>
      <div className="relative h-8">
        <div className="absolute top-1/2 -translate-y-1/2 h-5 rounded-md bg-slate-200/80 overflow-hidden" style={{ left: `${left}%`, width: `${width}%` }} title={`${fmt(p.planned_start)} → ${fmt(p.planned_end)} · ${p.progress}% (atteso ${p.expected}%)`}>
          <div className={`h-full ${st.bar} transition-[width] duration-500`} style={{ width: `${p.progress}%` }} />
          <div className="absolute inset-0 flex items-center px-1.5 text-[10px] font-mono font-semibold text-slate-900/80 whitespace-nowrap">{p.progress}%</div>
        </div>
      </div>
    </div>
  );
};

export const GanttChart = ({ siteId, site }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const savePhase = async (event) => {
    event.preventDefault();
    if (!editing || saving) return;
    if (editing.planned_end <= editing.planned_start) {
      setSaveError("La data finale deve essere successiva a quella iniziale.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      await api.updateGanttPhase(siteId, editing);
      setEditing(null);
      await load();
    } catch (error) {
      setSaveError(error?.response?.data?.detail || "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.siteGantt(siteId)); } catch { setData(null); } finally { setLoading(false); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-orange-500 mx-auto" /></div>;
  if (!data) return <p className="text-sm text-red-500">Impossibile caricare il cronoprogramma.</p>;
  const span = Math.max(dayDiff(data.start, data.end), 1);
  const todayPct = Math.min(Math.max((dayDiff(data.start, data.today) / span) * 100, 0), 100);
  const ticks = monthTicks(data.start, data.end);

  if (!data.has_offers || data.phases.length === 0) {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="gantt-empty">
          <CalendarRange className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          {!data.has_offers ? <>Nessuna offerta collegata: associa un'offerta con computo dalla scheda <span className="font-semibold">Offerte</span> per generare il cronoprogramma.</> : <>Il computo delle offerte collegate non ha ancora voci: compila il CME per vedere le fasi.</>}
        </div>
        <WeeklyWorkPlan siteId={siteId} phases={[]} onChanged={load} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gantt-panel">
      <Summary s={data.summary} data={data} />
      <WeeklyWorkPlan siteId={siteId} phases={data.phases} onChanged={load} />
      {editing && (
        <form onSubmit={savePhase} className="bg-white border border-orange-200 rounded-xl p-4 space-y-3" data-testid="gantt-phase-editor">
          <div className="flex items-center justify-between"><strong className="text-sm">Pianifica: {editing.name}</strong><button type="button" onClick={() => { setEditing(null); setSaveError(""); }} aria-label="Chiudi editor"><X className="w-4 h-4" /></button></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs font-medium text-slate-600">Inizio previsto<input type="date" required value={editing.planned_start} onChange={(e) => setEditing((old) => ({ ...old, planned_start: e.target.value }))} className="mt-1 w-full rounded border border-slate-300 p-2" /></label>
            <label className="text-xs font-medium text-slate-600">Fine prevista<input type="date" required value={editing.planned_end} onChange={(e) => setEditing((old) => ({ ...old, planned_end: e.target.value }))} className="mt-1 w-full rounded border border-slate-300 p-2" /></label>
          </div>
          {saveError && <p role="alert" className="text-xs text-red-600">{saveError}</p>}
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-orange-600 text-white px-3 py-2 text-sm disabled:opacity-50"><Save className="w-4 h-4" />{saving ? "Salvataggio..." : "Salva pianificazione"}</button>
        </form>
      )}
      {data.estimated_dates && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="gantt-estimated-notice">
          <Info className="w-4 h-4 shrink-0 mt-0.5" /> Date stimate: imposta inizio e fine lavori in <span className="font-semibold">Modifica cantiere</span> per un programma preciso. Le fasi seguono l'ordine e il peso economico delle categorie del computo.
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="font-display font-semibold text-slate-900 inline-flex items-center gap-2"><CalendarRange className="w-4 h-4 text-orange-600" /> Cronoprogramma</div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            {Object.entries(STATUS).map(([k, v]) => <span key={k} className="inline-flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-sm ${v.bar}`} /> {v.label}</span>)}
            <button onClick={load} data-testid="gantt-refresh" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Aggiorna"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[640px] px-2 pb-3 relative">
            <div className="grid grid-cols-[minmax(160px,260px)_1fr] gap-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold pl-2">Fase (categoria CME)</div>
              <div className="relative h-5 text-[10px] text-slate-400 font-mono">
                <span className="absolute left-0">{fmt(data.start)}</span>
                {ticks.map((t) => <span key={t.label} className="absolute -translate-x-1/2" style={{ left: `${t.left}%` }}>{t.label}</span>)}
                <span className="absolute right-0">{fmt(data.end)}</span>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 pointer-events-none grid grid-cols-[minmax(160px,260px)_1fr] gap-3 w-full">
                <div />
                <div className="relative">
                  {ticks.map((t) => <div key={t.label} className="absolute inset-y-0 border-l border-dashed border-slate-200" style={{ left: `${t.left}%` }} />)}
                  <div className="absolute inset-y-0 border-l-2 border-orange-500 z-10" style={{ left: `${todayPct}%` }} data-testid="gantt-today-line"><span className="absolute -top-0.5 -translate-x-1/2 text-[9px] font-bold text-orange-600 bg-white px-1 rounded">oggi</span></div>
                </div>
              </div>
              {data.phases.map((p) => <div key={p.name}><PhaseRow p={p} data={data} span={span} onEdit={(phase) => { setEditing({ name: phase.name, planned_start: phase.planned_start, planned_end: phase.planned_end }); setSaveError(""); }} />{(p.work_items || []).map(work => <WorkRow key={work.id} work={work} data={data} span={span} />)}</div>)}
              {(data.unassigned_work || []).length > 0 && <div className="text-xs font-semibold text-slate-500 pl-3 pt-3">Lavorazioni senza fase CME</div>}
              {(data.unassigned_work || []).map(work => <WorkRow key={work.id} work={work} data={data} span={span} />)}
            </div>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">Le percentuali si aggiornano spuntando <span className="font-semibold">Eseguita</span> sulle voci del computo{data.phases[0]?.offer_ids?.[0] && <> (<Link to={`/offerte/${data.phases[0].offer_ids[0]}`} className="text-orange-600 hover:underline" data-testid="gantt-open-cme">apri il computo</Link>)</>}. Una fase è in ritardo se supera la data di fine prevista o resta oltre 10 punti sotto l'avanzamento atteso.</p>
    </div>
  );
};
